import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { request } from 'services/apis';

export interface RowData {
  [key: string]: string;
  Leaves: string;
}

export interface AttendanceRecord {
  id: string;
  employeeName: string;
  projectName: string;
  monthTotal: number;
  rowData: RowData;
}

export interface AttendanceRecordResponse {
  id: string;
  monthName: string;
  year: number;
  holidays: string;
  workingDays: number;
  attendanceRecords: AttendanceRecord[];
}

export interface AttendanceState {
  attendanceData: AttendanceRecordResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: AttendanceState = {
  attendanceData: null,
  loading: false,
  error: null
};

export const fetchAttendanceRecords = createAsyncThunk(
  'attendance/fetchRecords',
  async (
    { month, year }: { month: string; year: number },
    { rejectWithValue }
  ) => {
    try {
      const response = await request.get<AttendanceRecordResponse>(
        `/Attendance/month-details?monthName=${month.toLowerCase()}&year=${year}`
      );
      return response.data;
    } catch (error: any) {
      toast.info('No data available for the selected month');
      const errorMessage = `No data available for ${month} ${year}`;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteAttendanceRecord = createAsyncThunk(
  'attendance/deleteRecord',
  async (recordId: string, { rejectWithValue }) => {
    try {
      const response = await request.delete(
        `/Attendance/delete-month/${recordId}`
      );
      toast.success('Attendance record deleted successfully');
      return response.data;
    } catch (error: any) {
      console.error('Delete attendance record error:', error);
      toast.error('Error deleting attendance record');
      return rejectWithValue(error.message || 'Delete failed');
    }
  }
);

const attendanceSlice = createSlice({
  name: 'attendances',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttendanceRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAttendanceRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.attendanceData = action.payload;
        state.error = null;
      })
      .addCase(fetchAttendanceRecords.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) ||
          'No data available for the selected month';
        state.attendanceData = null;
      })
      .addCase(deleteAttendanceRecord.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAttendanceRecord.fulfilled, (state) => {
        state.loading = false;
        state.attendanceData = null;
        state.error = null;
      })
      .addCase(deleteAttendanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || 'Error deleting attendance record';
      });
  }
});

const { reducer } = attendanceSlice;

export default reducer;
